import { useEffect } from 'react';
import { AppShell, Badge, Burger, Button, Group, NavLink, ScrollArea, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconArchive,
  IconBell,
  IconBellCog,
  IconBuildingWarehouse,
  IconCalendarTime,
  IconCalendarX,
  IconChartBar,
  IconClipboardCheck,
  IconFileCertificate,
  IconHistory,
  IconListDetails,
  IconLock,
  IconRulerMeasure,
  IconShieldLock,
  IconTruck,
  IconUsers,
  IconLogout,
  IconMask,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { hasCapability, type Actor, type Capability, type PolicyStatus } from '@prooffleet/contracts';
import { api, useSession } from '@prooffleet/client';
import { Defects } from './screens/Defects.js';
import { Feed } from './screens/Feed.js';
import { Login } from './screens/Login.js';
import { Missed } from './screens/Missed.js';
import { Notifications } from './screens/Notifications.js';
import { Roles } from './screens/Roles.js';
import { Sites } from './screens/Sites.js';
import { Units } from './screens/Units.js';
import { Templates } from './screens/Templates.js';
import { Thresholds } from './screens/Thresholds.js';
import { Schedules } from './screens/Schedules.js';
import { Policy } from './screens/Policy.js';
import { Audit } from './screens/Audit.js';
import { Retention } from './screens/Retention.js';
import { Security } from './screens/Security.js';
import { Compliance } from './screens/Compliance.js';
import { NotificationSettings } from './screens/NotificationSettings.js';
import { UnitCard } from './screens/UnitCard.js';
import { Users } from './screens/Users.js';
import { AcceptInvite } from './screens/AcceptInvite.js';

interface MePayload {
  actor: Actor;
  policy: PolicyStatus;
  impersonatorUserId: string | null;
}

/**
 * Меню разделено на два блока: что происходит и как это настроено.
 * Смешивать их значило бы ставить «Пороги» рядом с «Дефектами», хотя
 * первое открывают раз в квартал, а второе — каждое утро.
 */
/**
 * Иконка помогает найти пункт периферийным зрением, когда их семнадцать.
 * Одно семейство на весь проект (Tabler), одна толщина линии — смесь наборов
 * читается как небрежность быстрее, чем любая другая деталь интерфейса.
 */
/**
 * Пункт меню знает право, которое ему нужно.
 *
 * Без этого меню показывало все семнадцать пунктов кому угодно, и половина
 * отвечала 403. В обычной работе это не всплывало — водитель портал не
 * открывает, — но вход от чужого имени показывает ровно то, что видит человек,
 * и пробел стал очевиден. `null` означает «доступно всем вошедшим»:
 * собственная безопасность и собственные настройки уведомлений.
 */
const NAV: {
  to: string;
  label: string;
  group: string;
  icon: typeof IconClipboardCheck;
  needs: Capability | null;
}[] = [
  { to: '/feed', label: 'Inspections', group: 'Operations', icon: IconClipboardCheck, needs: 'inspection:read' },
  { to: '/defects', label: 'Defects', group: 'Operations', icon: IconAlertTriangle, needs: 'defect:read' },
  { to: '/missed', label: 'Missed', group: 'Operations', icon: IconCalendarX, needs: 'inspection:read' },
  { to: '/compliance', label: 'Compliance', group: 'Operations', icon: IconChartBar, needs: 'inspection:read' },
  { to: '/notifications', label: 'Notifications', group: 'Operations', icon: IconBell, needs: null },
  { to: '/units', label: 'Units', group: 'Fleet', icon: IconTruck, needs: 'unit:read' },
  { to: '/sites', label: 'Sites', group: 'Fleet', icon: IconBuildingWarehouse, needs: 'site:read' },
  { to: '/templates', label: 'Templates', group: 'Setup', icon: IconListDetails, needs: 'template:write' },
  { to: '/thresholds', label: 'Thresholds', group: 'Setup', icon: IconRulerMeasure, needs: 'threshold:write' },
  { to: '/schedules', label: 'Schedules', group: 'Setup', icon: IconCalendarTime, needs: 'threshold:write' },
  { to: '/policy', label: 'Policy', group: 'Setup', icon: IconFileCertificate, needs: 'policy:write' },
  { to: '/users', label: 'People', group: 'Access', icon: IconUsers, needs: 'user:read' },
  { to: '/roles', label: 'Roles', group: 'Access', icon: IconShieldLock, needs: 'user:read' },
  { to: '/security', label: 'Security', group: 'Access', icon: IconLock, needs: null },
  { to: '/notification-settings', label: 'Alerts', group: 'Access', icon: IconBellCog, needs: null },
  { to: '/audit', label: 'Audit log', group: 'Records', icon: IconHistory, needs: 'audit:read' },
  { to: '/retention', label: 'Retention', group: 'Records', icon: IconArchive, needs: 'retention:configure' },
];

/** Одна толщина линии на весь интерфейс. */
const ICON = { size: 17, stroke: 1.6 } as const;

const NAV_GROUPS = ['Operations', 'Fleet', 'Setup', 'Access', 'Records'] as const;

export function App(): React.JSX.Element {
  const [opened, { toggle }] = useDisclosure();
  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken, actor, setSession, clear } = useSession();

  const { data, isLoading } = useQuery({
    queryKey: ['me', accessToken],
    queryFn: () => api<MePayload>('/auth/me'),
    retry: false,
  });

  useEffect(() => {
    if (data !== undefined) setSession(useSession.getState().accessToken ?? '', data.actor);
  }, [data, setSession]);

  const { data: unread } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => api<{ unread: number }>('/notifications?unread=true'),
    enabled: actor !== null,
    refetchInterval: 20_000,
  });

  // Приём приглашения идёт ДО проверки входа: у приглашённого учётной записи ещё нет.
  if (location.pathname === '/accept-invite') return <AcceptInvite />;

  if (isLoading && actor === null) return <Text p="md" c="dimmed">Loading…</Text>;
  if (actor === null) return <Login />;

  const impersonating = data?.impersonatorUserId != null;

  /** Возврат в свою учётную запись: перезагрузка по той же причине, что и вход. */
  const stopImpersonating = async (): Promise<void> => {
    const result = await api<{ accessToken: string; actor: Actor }>('/auth/impersonate/stop', {
      method: 'POST',
    });
    setSession(result.accessToken, result.actor);
    window.location.assign('/');
  };

  const logout = async (): Promise<void> => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      clear();
      void navigate('/');
    }
  };

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 208, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="var(--pf-gutter)"
    >
      <AppShell.Header
        style={{
          borderBottom: '1px solid var(--pf-line)',
          background: impersonating
            ? 'var(--mantine-color-orange-1)'
            : 'var(--mantine-color-white)',
        }}
      >
        <Group h="100%" px="lg" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Text fw={680} fz="md" style={{ letterSpacing: '-0.02em' }}>
              ProofFleet
            </Text>
            <Text className="pf-eyebrow">{actor.roleKey.replace('_', ' ')}</Text>
          </Group>
          <Group gap="sm" wrap="nowrap">
            {impersonating && (
              // Подмена обязана быть заметной постоянно, а не один раз при входе:
              // иначе легко забыть, под кем смотришь, и принять чужой экран за свой.
              <Group gap={8} wrap="nowrap">
                <IconMask size={16} stroke={ICON.stroke} />
                <Text fz="sm" fw={560}>
                  Viewing as {actor.roleKey.replace('_', ' ')}
                </Text>
                <Button
                  size="compact-sm"
                  variant="filled"
                  color="orange.8"
                  onClick={() => void stopImpersonating()}
                >
                  Return to my account
                </Button>
              </Group>
            )}
            <Button
              variant="subtle"
              color="gray"
              size="compact-sm"
              leftSection={<IconLogout size={15} stroke={ICON.stroke} />}
              onClick={() => void logout()}
            >
              Sign out
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar
        p="sm"
        style={{ borderRight: '1px solid var(--pf-line)', background: 'var(--mantine-color-white)' }}
      >
        <ScrollArea>
          <Stack gap="md">
            {NAV_GROUPS.map((group) => {
              const items = NAV.filter(
                (item) =>
                  item.group === group &&
                  (item.needs === null || hasCapability(actor, item.needs)),
              );
              // Пустая группа не рисуется: заголовок без пунктов читается как поломка.
              if (items.length === 0) return null;
              return (
              <Stack key={group} gap={2}>
                <Text className="pf-eyebrow" pl="xs" pb={2}>
                  {group}
                </Text>
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    className="pf-nav-link"
                    component={Link}
                    to={item.to}
                    label={item.label}
                    active={location.pathname.startsWith(item.to)}
                    leftSection={<item.icon size={ICON.size} stroke={ICON.stroke} />}
                    rightSection={
                      item.to === '/notifications' && (unread?.unread ?? 0) > 0 ? (
                        <Badge size="xs" circle color="red.8" variant="filled" className="pf-num">
                          {unread?.unread}
                        </Badge>
                      ) : null
                    }
                  />
                ))}
              </Stack>
              );
            })}
          </Stack>
        </ScrollArea>
      </AppShell.Navbar>

      <AppShell.Main>
        <div className="pf-page">
        <Routes>
          <Route path="/" element={<Navigate to="/feed" replace />} />
          <Route path="/feed" element={<Feed />} />
          <Route path="/units" element={<Units />} />
          <Route path="/units/:unitId" element={<UnitCard />} />
          <Route path="/sites" element={<Sites />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/thresholds" element={<Thresholds />} />
          <Route path="/schedules" element={<Schedules />} />
          <Route path="/policy" element={<Policy />} />
          <Route path="/security" element={<Security />} />
          <Route path="/compliance" element={<Compliance />} />
          <Route path="/notification-settings" element={<NotificationSettings />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/retention" element={<Retention />} />
          <Route path="/defects" element={<Defects />} />
          <Route path="/missed" element={<Missed />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/users" element={<Users />} />
          <Route path="/roles" element={<Roles />} />
          <Route path="*" element={<Navigate to="/feed" replace />} />
        </Routes>
        </div>
      </AppShell.Main>
    </AppShell>
  );
}
